import React, { useContext } from 'react';
import { ampli } from '../../ampli';
import { CurrentUser } from '../../contexts/CurrentUser';

// Amplitude events for Osavul Copilot. Names follow the app's human-readable
// "Verb object" convention ('Open case', 'Export narrative', …); property keys
// are snake_case. Every event carries user_id / workspace_id / is_super_admin
// plus narrative_id, context ('case' | 'dashboard') and session_id — the
// copilot session id, so all events of one conversation can be joined.
// Prompt and response text are intentionally never sent; only canned
// suggestion labels and lengths are.
export type CopilotEvent =
  | 'Open copilot'
  | 'Close copilot'
  | 'Submit copilot prompt'
  | 'Receive copilot response'
  | 'Copilot response error'
  | 'Stop copilot generation'
  | 'Start new copilot chat'
  | 'Rate copilot response'
  | 'Download copilot report'
  | 'Preview copilot report'
  | 'Open copilot references'
  | 'Export copilot chart'
  | 'Copy copilot response';

export type CopilotEventProps = Record<string, string | number | boolean | null | undefined>;

export function trackCopilotEvent(
  event: CopilotEvent,
  props: CopilotEventProps,
  user?: CurrentUser | null,
): void {
  ampli.track({
    event_type: event,
    event_properties: {
      user_id: user?.id,
      workspace_id: user?.workspace_id,
      is_super_admin: user?.is_super_admin ?? false,
      ...props,
    },
  });
}

export type CopilotTrack = (event: CopilotEvent, props?: CopilotEventProps) => void;

// The public component provides a tracker pre-bound to the open copilot
// (narrative / context / session) so nested pieces — message actions, chart
// blocks, report cards — fire events without threading props down. Outside
// the provider (unit renders) the hook is a no-op.
const noop: CopilotTrack = () => {};
export const CopilotAnalyticsContext = React.createContext<CopilotTrack>(noop);

export function useCopilotTrack(): CopilotTrack {
  return useContext(CopilotAnalyticsContext);
}

// Response-content flags derived from the final assistant text.
const REPORT_LINK_RE = /\/(?:dl|reports)\/\d+\/[0-9a-f-]{36}/i;
const CHART_BLOCK_RE = /```echarts/;
const REFERENCE_LINK_RE = /\(#ref-/;

export function describeResponse(text: string): CopilotEventProps {
  return {
    response_length: text.length,
    has_report: REPORT_LINK_RE.test(text),
    has_chart: CHART_BLOCK_RE.test(text),
    has_references: REFERENCE_LINK_RE.test(text),
  };
}

// count from a `#ref-publications?count=12&…` href built by the agent.
export function parseReferenceCount(href: string | undefined): number {
  const params = new URLSearchParams(href?.split('?')[1] ?? '');
  return Number(params.get('count')) || 0;
}
